// ════════════════════════════════════════════════════════════════════════════
//  Contagem cíclica (Cycle Counting) — gerar → contar → submeter → aprovar/rejeitar
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, ClipboardList, Eye } from 'lucide-react';
import { getApiErrorMessage } from '@/lib/apiClient';
import { cycleService as svc } from '@/features/wms/wmsService';
import { useLocalEstoqueCombo } from '@/features/estoque/hooks/useLocalEstoque';
import { LocalEstoqueSelect } from '@/components/LocalEstoqueSelect';
import { FiltroPeriodo } from '@/components/FiltroPeriodo';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';
import { usePaginacaoServidor } from '@/hooks/usePaginacaoServidor';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const ABC = ['Todos', 'A', 'B', 'C'];
const SENT = '__none__';
const STATUS_BADGE: Record<number, string> = {
  0: 'bg-blue-500/10 text-blue-600', 1: 'bg-amber-500/10 text-amber-600',
  2: 'bg-green-500/10 text-green-600', 3: 'bg-red-500/10 text-red-600', 4: 'bg-slate-500/10 text-slate-500',
};
const STATUS_OPCOES = [
  { id: 0, label: 'Em contagem' }, { id: 1, label: 'Aguardando aprovação' }, { id: 2, label: 'Aprovada' },
  { id: 3, label: 'Rejeitada' }, { id: 4, label: 'Cancelada' },
];
const fmt = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

export default function ContagensCiclo() {
  const qc = useQueryClient();
  // Filtros (FrmAprovacaoAjusteEstoque): status, local, período; paginação no servidor.
  const [statusId, setStatusId] = useState(-1);
  const [localFiltro, setLocalFiltro] = useState<number | undefined>();
  const [periodo, setPeriodo] = useState({ inicio: '', fim: '' });
  const filtros = { localEstoqueId: localFiltro, dtInicio: periodo.inicio || undefined, dtFim: periodo.fim || undefined };
  const pag = usePaginacaoServidor({ resetarQuando: [statusId, filtros] });
  const { data: lista } = useQuery({
    queryKey: ['contagens-ciclo', statusId, filtros, pag.pagina, pag.tamanhoPagina],
    queryFn: () => svc.listar(pag.pagina, pag.tamanhoPagina, statusId, filtros),
    placeholderData: keepPreviousData,
  });
  const { data: locais = [] } = useLocalEstoqueCombo();
  const [gerarOpen, setGerarOpen] = useState(false);
  const [detalheId, setDetalheId] = useState<number | null>(null);

  const inval = () => {
    qc.invalidateQueries({ queryKey: ['contagens-ciclo'] });
    if (detalheId) qc.invalidateQueries({ queryKey: ['contagem', detalheId] });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Contagem Cíclica / Aprovação de Ajuste</h1>
          <p className="text-muted-foreground mt-1">Conte parte do estoque (ABC) e ajuste o saldo só após aprovação.</p>
        </div>
        <Button className="gap-2" onClick={() => setGerarOpen(true)}><Plus className="h-4 w-4" />Gerar Contagem</Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><ClipboardList className="h-5 w-5" />Contagens</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-52 space-y-1"><Label className="text-xs text-muted-foreground">Status</Label>
              <Select value={String(statusId)} onValueChange={(v) => setStatusId(Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="-1">Todos</SelectItem>
                  {STATUS_OPCOES.map(o => <SelectItem key={o.id} value={String(o.id)}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div className="w-52 space-y-1"><Label className="text-xs text-muted-foreground">Local</Label>
              <LocalEstoqueSelect value={localFiltro} onChange={setLocalFiltro} rotuloTodos="Todos os locais" /></div>
            <FiltroPeriodo inicio={periodo.inicio} fim={periodo.fim} onChange={setPeriodo} rotulo="Data da contagem" />
          </div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Nº</TableHead><TableHead>Local</TableHead><TableHead>Classe</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Contados/Itens</TableHead><TableHead>Data</TableHead><TableHead className="text-right">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {!lista?.itens.length ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Nenhuma contagem</TableCell></TableRow>
              ) : lista.itens.map(c => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">{c.numero}</TableCell>
                  <TableCell>{c.localEstoque ?? '—'}</TableCell>
                  <TableCell><Badge variant="secondary">{c.classificacaoAbc}</Badge></TableCell>
                  <TableCell><Badge className={STATUS_BADGE[c.statusId]}>{c.status}</Badge></TableCell>
                  <TableCell className="text-right">{c.qtdContados}/{c.qtdItens}</TableCell>
                  <TableCell>{new Date(c.dtContagem).toLocaleDateString('pt-BR')}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" className="gap-1" onClick={() => setDetalheId(c.id)}><Eye className="h-4 w-4" />Abrir</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <PaginacaoRodape {...pag.rodape(lista)} />
        </CardContent>
      </Card>

      {gerarOpen && <GerarDialog locais={locais} onClose={() => setGerarOpen(false)} onSaved={(id) => { setGerarOpen(false); inval(); setDetalheId(id); }} />}
      {detalheId && <DetalheDialog id={detalheId} onClose={() => setDetalheId(null)} onChanged={inval} />}
    </div>
  );
}

function GerarDialog({ locais, onClose, onSaved }: {
  locais: { id: number; descricao: string }[]; onClose: () => void; onSaved: (id: number) => void;
}) {
  const [local, setLocal] = useState<number | null>(null);
  const [abc, setAbc] = useState('Todos');
  const gerar = useMutation({
    mutationFn: () => svc.gerar(null, local!, abc),
    onSuccess: (r) => { toast.success(r.mensagem); onSaved(r.id); }, onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Gerar Contagem Cíclica</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Local</Label>
            <Select value={local ? String(local) : SENT} onValueChange={(v) => setLocal(v === SENT ? null : Number(v))}>
              <SelectTrigger><SelectValue placeholder="Selecione o local" /></SelectTrigger>
              <SelectContent>{locais.map(l => <SelectItem key={l.id} value={String(l.id)}>{l.descricao}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Classe ABC</Label>
            <Select value={abc} onValueChange={setAbc}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{ABC.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select></div>
          <p className="text-xs text-muted-foreground">Os produtos com saldo no local (filtrados pela classe) entram na contagem.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={!local || gerar.isPending} onClick={() => gerar.mutate()}>Gerar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetalheDialog({ id, onClose, onChanged }: { id: number; onClose: () => void; onChanged: () => void; }) {
  const { data: c, refetch } = useQuery({ queryKey: ['contagem', id], queryFn: () => svc.obter(id) });
  const [contagens, setContagens] = useState<Record<number, number>>({});
  const [motivo, setMotivo] = useState('');

  const after = (m: string) => { toast.success(m); refetch(); onChanged(); };
  const err = (e: unknown) => toast.error(getApiErrorMessage(e));
  const mContar = useMutation({
    mutationFn: () => svc.contagem(id, Object.entries(contagens).map(([itemId, q]) => ({ itemId: Number(itemId), qtdContada: q }))),
    onSuccess: (r) => after(r.mensagem), onError: err,
  });
  const mSubmeter = useMutation({ mutationFn: () => svc.submeter(id), onSuccess: (r) => after(r.mensagem), onError: err });
  const mAprovar = useMutation({ mutationFn: () => svc.aprovar(id), onSuccess: (r) => after(r.mensagem), onError: err });
  const mRejeitar = useMutation({ mutationFn: () => svc.rejeitar(id, motivo), onSuccess: (r) => after(r.mensagem), onError: err });
  const mCancelar = useMutation({ mutationFn: () => svc.cancelar(id), onSuccess: (r) => after(r.mensagem), onError: err });

  if (!c) return null;
  const emContagem = c.statusId === 0;
  const aguardando = c.statusId === 1;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2">
          Contagem nº {c.numero}<Badge className={STATUS_BADGE[c.statusId]}>{c.status}</Badge>
          <span className="text-sm font-normal text-muted-foreground">{c.localEstoque} · classe {c.classificacaoAbc}</span>
        </DialogTitle></DialogHeader>

        <Table>
          <TableHeader><TableRow>
            <TableHead>Produto</TableHead><TableHead className="text-right">Saldo sistema</TableHead>
            <TableHead className="w-32 text-right">Contado</TableHead><TableHead className="text-right">Diverg.</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {c.itens.map(it => {
              const contado = contagens[it.id] ?? it.qtdContada;
              const div = contado - it.saldoSistema;
              return (
                <TableRow key={it.id}>
                  <TableCell>{it.produto ?? '—'}</TableCell>
                  <TableCell className="text-right">{fmt(it.saldoSistema)}</TableCell>
                  <TableCell>
                    {emContagem
                      ? <Input type="number" className="h-8 text-right" value={contagens[it.id] ?? it.qtdContada}
                          onChange={e => setContagens({ ...contagens, [it.id]: Number(e.target.value) })} />
                      : <div className="text-right">{fmt(it.qtdContada)}</div>}
                  </TableCell>
                  <TableCell className={`text-right ${div < 0 ? 'text-destructive' : div > 0 ? 'text-amber-600' : ''}`}>{fmt(emContagem ? div : it.divergencia)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {c.motivoRejeicao && <p className="text-sm text-destructive">Rejeitada: {c.motivoRejeicao}</p>}

        {emContagem && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => mContar.mutate()} disabled={mContar.isPending}>Salvar contagem</Button>
            <Button onClick={() => mSubmeter.mutate()} disabled={mSubmeter.isPending}>Submeter p/ aprovação</Button>
            <Button variant="ghost" className="text-destructive" onClick={() => mCancelar.mutate()} disabled={mCancelar.isPending}>Cancelar</Button>
          </div>
        )}
        {aguardando && (
          <div className="space-y-2 border-t pt-3">
            <div className="flex gap-2">
              <Button onClick={() => mAprovar.mutate()} disabled={mAprovar.isPending}>Aprovar (ajusta saldo)</Button>
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1"><Label className="text-xs">Motivo da rejeição</Label>
                <Input value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Motivo" /></div>
              <Button variant="destructive" disabled={!motivo || mRejeitar.isPending} onClick={() => mRejeitar.mutate()}>Rejeitar</Button>
            </div>
          </div>
        )}

        <DialogFooter><Button variant="outline" onClick={onClose}>Fechar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
